import { DigitacaoFormField } from '../../../../../models/SimulationBank/SimulationBankModels';
import { applyMaskCPF, applyMaskPhone, applyMaskCEP } from '../utils/masks';

export interface DigitacaoFormTab {
    key: string;
    label: string;
    fields: DigitacaoFormField[];
}

const TAB_ORDER = [
    'dados_cliente.pessoais',
    'dados_cliente.rg',
    'dados_cliente.contatos',
    'dados_cliente.endereco',
    'dados_cliente.profissionais',
    'dados_cliente.bancarios',
    'dados_vendedor',
];

const TAB_LABELS: Record<string, string> = {
    'dados_cliente.pessoais': 'Pessoais',
    'dados_cliente.rg': 'Documento (RG)',
    'dados_cliente.contatos': 'Contatos',
    'dados_cliente.endereco': 'Endereço',
    'dados_cliente.profissionais': 'Profissionais',
    'dados_cliente.bancarios': 'Bancários',
    'dados_vendedor': 'Vendedor',
    'dados_operacao': 'Operação',
};

// Remove acentos/caixa para comparar textos vindos de fontes diferentes (ex.: nome de
// cidade do ViaCEP vs. o innerText das opções do select de cada financeira).
export const normalizeText = (value: string): string =>
    value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

// A jornada guarda o estado sempre em sigla (ver handleStateChange no Step1Form), mas o
// select de UF do formulário de digitação varia por financeira: umas devolvem a sigla no
// value, outras o nome por extenso, outras um código interno com o nome só no innerText.
// Sem a tradução sigla ↔ nome não há como casar os dois lados.
export const UF_NAMES: Record<string, string> = {
    AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia',
    CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás',
    MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
    PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí',
    RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
    RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo',
    SE: 'Sergipe', TO: 'Tocantins',
};

// Traduz um valor vindo de fora do schema (jornada, ViaCEP) para o `value` da opção
// equivalente no select da financeira, comparando contra value e innerText — e, no caso
// de UF, também contra a sigla/nome correspondente. Retorna null quando nenhuma opção
// casa: aí é melhor deixar o campo em branco para o parceiro escolher do que gravar um
// valor fora do domínio, que faz o select renderizar vazio e ainda assim seguir no payload.
export const resolveSelectOptionValue = (field: DigitacaoFormField, rawValue: unknown): string | null => {
    const options = field.option ?? [];
    if (!options.length || rawValue == null || rawValue === '') return null;

    const raw = normalizeText(String(rawValue));
    const candidates = [raw];

    const ufName = UF_NAMES[String(rawValue).trim().toUpperCase()];
    if (ufName) candidates.push(normalizeText(ufName));

    const ufSigla = Object.keys(UF_NAMES).find((sigla) => normalizeText(UF_NAMES[sigla]) === raw);
    if (ufSigla) candidates.push(normalizeText(ufSigla));

    const match = options.find(
        (o) => candidates.includes(normalizeText(o.value)) || candidates.includes(normalizeText(o.innerText))
    );
    return match ? match.value : null;
};

const GROUP_FIELD_PRIORITY: Record<string, string[]> = {
    'dados_cliente.pessoais': [
        'Nome Completo',
        'Estado Civil',
        'Gênero',
        'Nome da Mãe',
        'Pessoa Politicamente Exposta (PEP)',
        'Nacionalidade',
        'Cidade de Naturalidade',
        'UF de Naturalidade',
    ],
    'dados_cliente.rg': [
        'Número do RG',
        'Órgão Emissor',
        'UF de Emissão do RG',
        'Data de Emissão',
    ],
    'dados_cliente.endereco': [
        'CEP',
        'Logradouro',
        'Número',
        'Bairro',
        'Cidade',
    ],
    'dados_cliente.profissionais': [
        'Valor da Renda Comprovada (R$)',
        'Tipo de Ocupação',
        'Grau de Instrução',
    ],
    'dados_cliente.bancarios': ['Banco'],
    'dados_vendedor': ['Nome do Vendedor'],
};

const reorderGroupFields = (group: string, fields: DigitacaoFormField[]): DigitacaoFormField[] => {
    const priority = GROUP_FIELD_PRIORITY[group];
    if (!priority) return fields;

    const normalizedPriority = priority.map(normalizeText);
    const priorityIndex = (field: DigitacaoFormField): number => {
        const idx = normalizedPriority.indexOf(normalizeText(field.label));
        return idx === -1 ? normalizedPriority.length : idx;
    };

    return [...fields].sort((a, b) => priorityIndex(a) - priorityIndex(b));
};

export const getFieldGroup = (chave: string): string => chave.split('.').slice(0, -1).join('.');

export const getNestedValue = (obj: any, chave: string): any =>
    chave.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

// Escreve `value` em `obj` seguindo a mesma convenção de aninhamento por segmento de
// "chave" usada pelo restante do módulo (ver comentário de FormDataDigitacao). Muta e
// retorna `obj`, criando os níveis intermediários que faltarem.
export const setNestedValue = (obj: Record<string, any>, chave: string, value: any): Record<string, any> => {
    const keys = chave.split('.');
    let cursor = obj;
    keys.forEach((key, idx) => {
        if (idx === keys.length - 1) {
            cursor[key] = value;
            return;
        }
        if (cursor[key] == null || typeof cursor[key] !== 'object') cursor[key] = {};
        cursor = cursor[key];
    });
    return obj;
};

const prettifyGroupLabel = (group: string): string => {
    const last = group.split('.').pop() ?? group;
    return last.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const groupFieldsIntoTabs = (fields: DigitacaoFormField[]): DigitacaoFormTab[] => {
    const byGroup = new Map<string, DigitacaoFormField[]>();
    fields.forEach((field) => {
        if (field.chave.startsWith('dados_operacao')) return;

        const group = getFieldGroup(field.chave);
        if (!byGroup.has(group)) byGroup.set(group, []);
        byGroup.get(group)!.push(field);
    });

    const orderedKeys = [...TAB_ORDER.filter((k) => byGroup.has(k)), ...[...byGroup.keys()].filter((k) => !TAB_ORDER.includes(k))];

    return orderedKeys.map((key) => {
        const groupFields = byGroup.get(key)!;

        return {
            key,
            label: TAB_LABELS[key] ?? prettifyGroupLabel(key),
            fields: reorderGroupFields(key, groupFields),
        };
    });
};

export const getOptionLabel = (field: DigitacaoFormField, value: any): string => {
    if (value == null || value === '') return '—';
    const match = field.option?.find((o) => o.value === String(value));
    return match?.innerText ?? String(value);
};

export const getFieldMask = (chave: string): ((v: string) => string) | undefined => {
    const field = chave.split('.').pop() || '';
    if (field.includes('cep')) return applyMaskCEP;
    if (field.includes('cpf')) return applyMaskCPF;
    if (field.includes('telefone') || field.includes('celular')) return applyMaskPhone;
    return undefined;
};
